<template>
  <div class="dropdown" :class="dropdownClass">
    <button
      v-click-away="onClickAwayAway"
      class="gf_btn--dropdown"
      :class="bClass"
      :style="dropdownStyling"
      @click="isShowOptions = !isShowOptions">
      <i v-if="isIconForm" :class="selected.icon ? selected.icon : optionValue.icon" />
      <span v-if="visibleName" class="gf_dropdown__label" :class="{ 'gf-ml-8 gf-mr-8': isIconForm }">
        <div class="gf_truncate">
          {{ optionValue ? optionValue.name : '' }}
        </div>
        <i
          v-if="optionValue && optionValue.warning"
          class="icon-warn-triangle text-warning ml-4px mr-4px gfDropDownSelect__warning" />
      </span>
      <i :class="['icon', isShowOptions ? 'icon-chevron-fill-up' : 'icon-chevron-fill-down']" />
    </button>
    <perfect-scrollbar
      v-if="isShowOptions"
      class="gf_dropdown--options"
      :class="[fType, { 'gf_dropdown--options-scroll': isScrollDropdown }]"
      :style="dropdownStylingOption">
      <GpDropdownItem
        v-for="(option, index) in options"
        :key="index"
        :dropdown-styling-option-item="dropdownStylingOptionItem"
        :item-class="dropdownItemClass"
        :option="(option as any)"
        :f-type="fType"
        :default-value="value"
        @select-item="selectItem"
        @edit-item="editItem" />
      <div class="gf_dropdown--action">
        <slot name="footer"></slot>
      </div>
    </perfect-scrollbar>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent } from 'vue';
import GpDropdownItem from './GfDropdownItem.vue';

type DropdownOptionBasic = {
  name: string;
  value: string;
  icon: any;
  handleEdit: boolean;
  warning: any;
};

const DROPDOWN_TYPE = {
  SIMPLE_TYPE: 'simple',
  ICON_TYPE: 'icon',
};

export default defineComponent({
  name: 'GfDropdown',
  components: {
    GpDropdownItem,
  },
  props: {
    dropdownStyling: {
      type: Object,
      default: () => {},
    },
    dropdownStylingOption: {
      type: Object,
      default: () => {},
    },
    dropdownStylingOptionItem: {
      type: Object,
      default: () => {},
    },
    options: {
      type: Array as PropType<Array<DropdownOptionBasic>>,
      require: false,
      default: () => [],
    },
    fType: {
      type: String,
      default: 'icon',
    },
    value: {
      type: [String, Number],
      default: '',
    },
    dropdownClass: {
      type: String,
      default: '',
    },
    bClass: {
      type: String,
      default: '',
    },
    dropdownItemClass: {
      type: String,
      default: '',
    },
    visibleName: {
      type: Boolean,
      default: true,
    },
    isScrollDropdown: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['selected', 'handleEdit'],
  data() {
    return {
      isShowOptions: false,
      selected: {} as any,
    };
  },
  computed: {
    optionValue() {
      const option = this.options.find((option: any) => option.value === this.value) as DropdownOptionBasic;
      return option || {};
    },
    isIconForm() {
      return this.fType === DROPDOWN_TYPE.ICON_TYPE;
    },
  },
  created() {
    this.selected.value = this.value;
    if (this.isIconForm) {
      const optionSelected: any = this.options.find((option: any) => option.value == this.value);
      this.selected.icon = optionSelected && optionSelected.icon ? optionSelected.icon : '';
    }
  },
  methods: {
    onClickAwayAway() {
      // setTimeout: Fix click away
      setTimeout(() => {
        this.isShowOptions = false;
      }, 50);
    },
    selectItem(option: DropdownOptionBasic) {
      this.selected = option;
      this.$emit('selected', option);

      // setTimeout: Fix click away
      setTimeout(() => {
        this.isShowOptions = false;
      }, 50);
    },
    editItem(option: DropdownOptionBasic) {
      this.$emit('handleEdit', option);
    },
  },
});
</script>

<style scoped lang="scss">
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
.custom--hover {
  button {
    &:hover {
      border: 1px solid #d6d6d6;
    }
  }
}

.dropdown {
  position: relative;
}

.gf_global-style__preview-option_dropdown {
  .gf_btn--dropdown {
    height: 40px;
    border: 1px solid $black3;
    justify-content: space-around;
    width: 76px;
    background-color: $black3;
    font-size: 16px;

    &:hover {
      background: $black2;
      border-color: $black2;
    }
  }

  .gf_dropdown--options {
    background-color: $black2;
    top: 45px;
  }
}

.gf-ml-4 {
  margin-left: 4px;
}

.gf_btn--dropdown {
  @include flex-center;
  padding: 9px 8px;
  width: 98px;
  height: 32px;
  right: 0;
  top: 0;
  /* Hybrid Shade / Dark 400 */
  background: $black;
  /* Primary Blue/275 */
  border: 1px solid #547aff;
  border-radius: $radius;
  color: white;

  .gf_dropdown__label {
    display: flex;
    font-style: normal;
    font-weight: $weight-400;
    line-height: 14px;
    cursor: pointer;
    color: #e2e2e2;
    flex: none;
    order: 0;
    flex-grow: 1;
    text-align: left;
    max-width: calc(100% - 12px);
    height: 15px;

    &.gf-ml-8 {
      margin-left: 8px;
    }
    &.gf-mr-8 {
      margin-right: 8px;
    }
  }
}

.gf_dropdown--options {
  /* Auto layout */
  position: absolute;
  right: 0;
  padding: 8px;
  background: $black3;
  box-shadow: 0 12px 38px rgba(34, 34, 34, 0.0393604), 0 5.01331px 15.8755px rgba(34, 34, 34, 0.056545),
    0 2.68036px 8.4878px rgba(34, 34, 34, 0.07), 0 1.50259px 4.75819px rgba(34, 34, 34, 0.083455),
    0 0.798012px 2.52704px rgba(34, 34, 34, 0.10064), 0 0.332071px 1.05156px rgba(34, 34, 34, 0.14);
  border-radius: $radius;
  z-index: 1;
}

.gf_dropdown--options-scroll {
  max-height: 180px;
  overflow: auto;
}
</style>
